import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useShippingSettings } from '../hooks/useShippingSettings';
import { useAuth } from './AuthContext';
import { useAnalytics } from './AnalyticsContext';
import { announceToScreenReader } from '../utils/a11yHelpers';
import axios from 'axios';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => {
    // Generate or get session ID for guest users
    let id = localStorage.getItem('cart_session_id');
    if (!id) {
      id = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cart_session_id', id);
    }
    return id;
  });
  const { calculateShipping, calculateTax, settings: taxSettings } = useShippingSettings();
  const { user, loading: authLoading } = useAuth();
  const { trackEvent } = useAnalytics();

  // Load cart from API (works for both logged-in and guest users)
  const loadCart = useCallback(async () => {
    try {
      const response = await axios.get('/api/cart', {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      if (response.data.cart && response.data.cart.items) {
        // Transform database cart items to match frontend format
        const transformedItems = response.data.cart.items.map(item => ({
          id: item.id,
          product: item.product,
          bundle: item.bundle,
          is_bundle: item.is_bundle || false,
          bundle_discount: item.bundle_discount || 0,
          quantity: item.quantity,
          selectedOptions: item.selected_options || {},
          addedAt: item.added_at
        }));
        setItems(transformedItems);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setItems([]);
    }
  }, [user, sessionId]);

  // Load cart when component mounts
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Reload cart when user changes (login/logout)
  useEffect(() => {
    if (!authLoading) {
      loadCart();
    }
  }, [user, authLoading, loadCart]);

  // Note: Cart is now always managed by the API for both logged-in and guest users

  const addToCart = async (product, quantity = 1, selectedOptions = {}) => {
    // Prevent multiple simultaneous calls
    if (isLoading) {
      return;
    }
    
    // Check stock availability before sending to server
    if (product.stock_info) {
      if (!product.stock_info.canOrder) {
        const errorMessage = 'המוצר חסר במלאי';
        toast.error(errorMessage);
        announceToScreenReader(errorMessage, 'assertive');
        return;
      }
      
      // For agents with exact quantity, check if enough stock
      if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
        const existingCartItem = getCartItem(product.id, selectedOptions);
        const totalQuantityNeeded = existingCartItem ? existingCartItem.quantity + quantity : quantity;
        
        if (product.track_quantity && product.stock_info.quantity < totalQuantityNeeded) {
          const errorMessage = `אין מספיק מלאי. זמין במלאי: ${product.stock_info.quantity} יחידות`;
          toast.error(errorMessage);
          announceToScreenReader(errorMessage, 'assertive');
          return;
        }
      }
    } else if (user && (user.role === 'b2b' || user.role === 'company_admin' || user.role === 'company_user' || user.role === 'agent')) {
      // Fallback for old format
      const availableStock = product.stock_b2b || 0;
      const existingCartItem = getCartItem(product.id, selectedOptions);
      const totalQuantityNeeded = existingCartItem ? existingCartItem.quantity + quantity : quantity;
      
      if (product.track_quantity && availableStock < totalQuantityNeeded) {
        const errorMessage = `אין מספיק מלאי. זמין במלאי עסקי: ${availableStock} יחידות`;
        toast.error(errorMessage);
        announceToScreenReader(errorMessage, 'assertive');
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      // Always use API for both logged-in and guest users
      
      const response = await axios.post('/api/cart/items', {
        product_id: product.id,
        quantity,
        selected_options: selectedOptions
      }, {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      // Reload cart to get updated data
      await loadCart();
      
      // Track add to cart event
      trackEvent('add_to_cart', {
        product_id: product.id,
        product_name: product.name || product.name_en,
        product_sku: product.sku,
        price: product.price_b2c || product.price_b2b,
        quantity: quantity,
        currency: product.currency || 'ILS'
      });
      
      const successMessage = 'המוצר נוסף לעגלה';
      toast.success(successMessage);
      announceToScreenReader(successMessage, 'polite');
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Show specific error message from server if available
      const errorMessage = error.response?.data?.message || 'שגיאה בהוספה לעגלה';
      toast.error(errorMessage);
      announceToScreenReader(errorMessage, 'assertive');
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await axios.delete(`/api/cart/items/${itemId}`, {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      // Reload cart to get updated data
      await loadCart();
      const successMessage = 'המוצר הוסר מהעגלה';
      toast.success(successMessage);
      announceToScreenReader(successMessage, 'polite');
    } catch (error) {
      console.error('Error removing from cart:', error);
      const errorMessage = 'שגיאה בהסרה מהעגלה';
      toast.error(errorMessage);
      announceToScreenReader(errorMessage, 'assertive');
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }

    try {
      await axios.put(`/api/cart/items/${itemId}`, {
        quantity: quantity
      }, {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      // Reload cart to get updated data
      await loadCart();
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      const errorMessage = 'שגיאה בעדכון הכמות';
      toast.error(errorMessage);
      announceToScreenReader(errorMessage, 'assertive');
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete('/api/cart', {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      setItems([]);
      const successMessage = 'העגלה נוקתה';
      toast.success(successMessage);
      announceToScreenReader(successMessage, 'polite');
    } catch (error) {
      console.error('Error clearing cart:', error);
      const errorMessage = 'שגיאה בניקוי העגלה';
      toast.error(errorMessage);
      announceToScreenReader(errorMessage, 'assertive');
    }
  };

  const clearStaleCart = async () => {
    try {
      await axios.delete('/api/cart', {
        headers: {
          'x-session-id': sessionId,
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      setItems([]);
      toast.info('העגלה נוקתה בגלל מוצרים שאינם זמינים');
    } catch (error) {
      console.error('Error clearing stale cart:', error);
      // Fallback to local clearing
      setItems([]);
      toast.info('העגלה נוקתה בגלל מוצרים שאינם זמינים');
    }
  };

  // Debug function to check cart state
  const debugCart = () => {
    console.log('Cart Debug Info:', {
      itemsCount: items.length,
      items: items,
      isLoading: isLoading,
      sessionId: sessionId,
      user: user ? { id: user.id, role: user.role } : null,
      cartSessionId: localStorage.getItem('cart_session_id')
    });
  };

  const getItemCount = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getSubtotal = () => {
    return items.reduce((total, item) => {
      // Check if this is a bundle or a regular product
      const isBundle = item.is_bundle || item.bundle;
      
      if (isBundle) {
        // For bundles, use bundle_price
        const bundleData = item.bundle;
        if (!bundleData) {
          console.warn('getSubtotal: bundle data is null/undefined for item:', item);
          return total;
        }
        const price = parseFloat(bundleData.bundle_price) || 0;
        return total + (price * item.quantity);
      } else {
        // For regular products
        if (!item.product) {
          console.warn('getSubtotal: item.product is null/undefined for item:', item);
          return total;
        }
        
        // Determine the correct price based on user role
        let price = 0;
        
        if (user?.role === 'company_admin' || user?.role === 'company_user') {
          // Use B2B price for company users
          price = parseFloat(item.product.price_b2b) || parseFloat(item.product.price_b2c) || 0;
        } else {
          // Use B2C price for regular customers
          price = parseFloat(item.product.price_b2c) || 0;
        }
        
        // Handle contract pricing if available
        if (item.product.contractPrice) {
          price = parseFloat(item.product.contractPrice);
        }
        
        if (isNaN(price)) {
          console.warn('getSubtotal: price is NaN for product:', item.product.name, 'price_b2c:', item.product.price_b2c, 'price_b2b:', item.product.price_b2b);
          price = 0;
        }
        
        return total + (price * item.quantity);
      }
    }, 0);
  };

  const getTax = (subtotal) => {
    return calculateTax(subtotal);
  };

  const getShipping = (subtotal) => {
    return calculateShipping(subtotal);
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const tax = getTax(subtotal);
    const shipping = getShipping(subtotal);
    
    // If tax is included in prices, don't add it again
    if (taxSettings.taxIncluded) {
      return subtotal + shipping;
    } else {
      return subtotal + tax + shipping;
    }
  };

  const getCartSummary = () => {
    const subtotal = getSubtotal();
    const tax = getTax(subtotal);
    const shipping = getShipping(subtotal);
    
    // If tax is included in prices, don't add it again to total
    const total = taxSettings.taxIncluded ? 
      subtotal + shipping : 
      subtotal + tax + shipping;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      shipping: parseFloat(shipping.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      itemCount: getItemCount()
    };
  };

  const isInCart = (productId, selectedOptions = {}) => {
    return items.some(
      item => item.product?.id === productId &&
      JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
    );
  };

  const getCartItem = (productId, selectedOptions = {}) => {
    return items.find(
      item => item.product?.id === productId &&
      JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
    );
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(prev => !prev);

  const value = {
    items,
    cartItems: items, // Add alias for compatibility
    isOpen,
    isLoading,
    sessionId,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearStaleCart,
    getItemCount,
    getSubtotal,
    getTax,
    getShipping,
    getTotal,
    getTotalPrice: getTotal, // Add alias for compatibility
    getCartSummary,
    isInCart,
    getCartItem,
    openCart,
    closeCart,
    toggleCart,
    loadCart, // Expose loadCart for manual refresh
    debugCart // Add debug function
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
